import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ForwardingSettingsDTO } from '@amrita/shared';

export default function AdminSettingsPage() {
  const qc = useQueryClient();

  const { data: forwarding, isLoading } = useQuery<ForwardingSettingsDTO>({
    queryKey: ['forwarding-settings'],
    queryFn: () => api.get<ForwardingSettingsDTO>('/api/admin/forwarding'),
  });

  const { data: allSettings } = useQuery<Record<string, unknown>>({
    queryKey: ['admin-settings'],
    queryFn: () => api.get<Record<string, unknown>>('/api/admin/settings'),
  });

  const saveSetting = useMutation({
    mutationFn: (args: { key: string; value: unknown }) =>
      api.put<{ ok: true }>('/api/admin/settings', args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forwarding-settings'] });
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
    },
  });

  function toggleForwarding() {
    if (!forwarding) return;
    saveSetting.mutate({
      key: 'forwarding',
      value: { ...forwarding, enabled: !forwarding.enabled },
    });
  }

  function toggleModule(m: ForwardingSettingsDTO['modules'][number]) {
    if (!forwarding) return;
    const modules = forwarding.modules.includes(m)
      ? forwarding.modules.filter((x) => x !== m)
      : [...forwarding.modules, m];
    saveSetting.mutate({ key: 'forwarding', value: { ...forwarding, modules } });
  }

  const allModules: ForwardingSettingsDTO['modules'][number][] = [
    'content_studio',
    'photo_studio',
    'ai_chat',
    'training',
  ];

  return (
    <div className="safe-bottom min-h-full">
      <TopBar title="Настройки" />
      <div className="p-4 space-y-3">
        <Card className="space-y-3">
          <div className="font-semibold">Forwarding генераций</div>
          {isLoading && <div className="text-tg-hint text-sm">Загрузка…</div>}
          {forwarding && (
            <>
              <div className="text-sm text-tg-hint">Группа: {forwarding.groupId}</div>
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  Статус:{' '}
                  <span className={forwarding.enabled ? 'text-green-500' : 'text-tg-hint'}>
                    {forwarding.enabled ? 'Включено' : 'Выключено'}
                  </span>
                </span>
                <Button variant={forwarding.enabled ? 'secondary' : 'primary'} onClick={toggleForwarding}>
                  {forwarding.enabled ? 'Выключить' : 'Включить'}
                </Button>
              </div>

              <div className="pt-2 border-t border-tg-secondaryBg">
                <div className="text-sm mb-2">Модули:</div>
                <div className="grid grid-cols-2 gap-2">
                  {allModules.map((m) => {
                    const on = forwarding.modules.includes(m);
                    return (
                      <button
                        key={m}
                        onClick={() => toggleModule(m)}
                        className={
                          'px-3 py-2 rounded-lg text-sm border transition ' +
                          (on
                            ? 'bg-brand-orange/10 border-brand-orange/40 text-brand-orange'
                            : 'bg-tg-secondaryBg border-transparent text-tg-hint')
                        }
                      >
                        {on ? '☑' : '☐'} {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              {forwarding.exclusions.length > 0 && (
                <div className="text-xs text-tg-hint pt-1">
                  Исключения: {forwarding.exclusions.map((e) => '@' + e).join(', ')}
                </div>
              )}
            </>
          )}
        </Card>

        {allSettings && Object.keys(allSettings).length > 0 && (
          <Card>
            <div className="font-semibold mb-2">Все настройки (raw)</div>
            <pre className="text-xs overflow-x-auto bg-tg-secondaryBg/60 p-2 rounded">
              {JSON.stringify(allSettings, null, 2)}
            </pre>
          </Card>
        )}
      </div>
    </div>
  );
}
